import {
  Button,
  Input,
  makeStyles,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeftFilled,
  ArrowRightFilled,
  Search20Regular,
} from '@fluentui/react-icons';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchRecordingsQuery } from '../../queries/recording';
import { useGetUserPreferencesQuery } from '../../queries/user';
import { usePagination } from '../../utils/use-pagination';
import { RecordingCard } from '../recording-card';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  searchInput: {
    flex: 1,
    maxWidth: '480px',
    '& input': {
      borderRadius: tokens.borderRadiusXLarge,
    },
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    flex: 1,
    minWidth: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalM,
  },
  cardWrapper: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    flexShrink: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
    flex: 1,
  },
  emptyIcon: {
    fontSize: '64px',
    opacity: 0.4,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalL,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: `0 -2px 8px ${tokens.colorNeutralShadowAmbient}`,
  },
  paginationText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
    minWidth: '120px',
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: tokens.spacingVerticalXXL,
  },
});

interface RecordingsListProps {
  folderId?: string;
  activeRecordingId?: string;
  onOpenDrawer: (id: string) => void;
  onOpenNewWindow: (id: string) => void;
  onAssignFolder?: (recordingId: string, currentFolderId?: string) => void;
}

export const RecordingsList = ({
  folderId,
  activeRecordingId,
  onOpenDrawer,
  onOpenNewWindow,
  onAssignFolder,
}: RecordingsListProps) => {
  const styles = useStyles();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { data: userPreferences } = useGetUserPreferencesQuery();
  const isOneDriveSyncEnabled = userPreferences?.oneDriveSync?.enabled ?? false;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when folder changes
  useEffect(() => {
    setPage(1);
  }, [folderId]);

  const { data, isPending, error } = useSearchRecordingsQuery(
    debouncedSearch,
    page,
    folderId
  );

  const recordings = useMemo(() => data?.recordings || [], [data]);

  const {
    currentPage,
    totalPages,
    setNextPage,
    setPreviousPage,
    nextEnabled,
    previousEnabled,
  } = usePagination(data?.total || 0, 20);

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  return (
    <div className={styles.container}>
      {/* Search Bar */}
      <div className={styles.searchBar}>
        <Input
          className={styles.searchInput}
          contentBefore={<Search20Regular />}
          placeholder="Rechercher une réunion..."
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value)}
        />
      </div>

      {/* List */}
      <div className={styles.listContainer}>
        {isPending ? (
          <div className={styles.loadingContainer}>
            <Spinner size="large" label="Chargement des réunions..." />
          </div>
        ) : error ? (
          <div className={styles.emptyState}>
            <Text>Erreur lors du chargement des réunions</Text>
          </div>
        ) : recordings.length === 0 ? (
          <motion.div
            className={styles.emptyState}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Text size={400}>Aucune réunion trouvée</Text>
            <Text size={200}>
              {debouncedSearch
                ? 'Essayez une autre recherche'
                : folderId === 'unclassified'
                ? 'Toutes vos réunions sont classées dans des dossiers'
                : folderId
                ? 'Ce dossier est vide'
                : 'Invitez le bot a rejoindre votre prochaine réunion'}
            </Text>
          </motion.div>
        ) : (
          <AnimatePresence mode="popLayout">
            {recordings.map((recording, index) => (
              <div key={recording.id} className={styles.cardWrapper}>
                <RecordingCard
                  recording={recording}
                  isActive={activeRecordingId === recording.id}
                  isOneDriveSyncEnabled={isOneDriveSyncEnabled}
                  onOpenDrawer={onOpenDrawer}
                  onOpenNewWindow={onOpenNewWindow}
                  onAssignFolder={onAssignFolder}
                  index={index}
                />
              </div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftFilled />}
            onClick={setPreviousPage}
            disabled={!previousEnabled}
          />
          <Text className={styles.paginationText}>
            Page {currentPage} sur {totalPages}
          </Text>
          <Button
            appearance="subtle"
            icon={<ArrowRightFilled />}
            onClick={setNextPage}
            disabled={!nextEnabled}
          />
        </div>
      )}
    </div>
  );
};
